// Photon (photon.komoot.io) -- the OSM-backed geocoder chosen because it sends
// CORS headers (browser-callable, no proxy) and needs no API key. Two calls,
// both of them public API of this module and nothing else:
//
//   geocodeName    -- one name -> one position, for the explicit "Locate on
//                     map" action. limit=1, 5s budget: a deliberate click is
//                     worth waiting for.
//   suggestGeocode -- a partial name -> a handful of candidates, for the name
//                     box's typeahead. limit=5, 3s budget: a suggest request
//                     that hasn't answered in three seconds has been
//                     superseded several keystrokes ago.
//
// Photon is built FOR autocomplete, so the second call is what it's for, but
// the public instance is a courtesy: the throttling that keeps volume sane
// (debounce, minimum characters, per-query caching) lives above this module,
// in PlaceSuggestInput and places.mjs. See README "Trip map".
//
// This is also the only module that knows Photon's wire format. If the public
// instance ever degrades, swapping in a route on the weather-proxy Lambda
// (server-side Nominatim with a proper User-Agent) is a change to this file.

const PHOTON_URL = 'https://photon.komoot.io/api/';
const TIMEOUT_MS = 5000;
const SUGGEST_TIMEOUT_MS = 3000;

// GeoJSON coordinates are [lon, lat] -- shared by both paths so they can't
// disagree about what counts as a usable position.
function coordinatesOf(feature) {
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const [lon, lat] = coordinates;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  return { lat, lon };
}

// A Photon feature -> the suggestion shape the name box renders, or null for
// anything unusable. Photon answers address-shaped queries with
// housenumber/street features that have no `name` at all; those aren't places
// a waypoint can be called, so they're dropped rather than rendered blank.
export function placeFromPhotonFeature(feature) {
  const position = coordinatesOf(feature);
  if (!position) return null;

  const properties = feature?.properties || {};
  const name = (properties.name || '').trim();
  if (!name) return null;

  // Enough to tell the many Springfields apart. Display only -- a pick
  // commits the bare `name` (see PlaceSuggestInput's header for why).
  const context =
    [properties.city, properties.state, properties.country]
      .filter((part) => part && part !== name)
      .join(', ') || undefined;

  // The `photon:` prefix is what guarantees a suggestion's React key can
  // never collide with a stored TaskPogoPlace's id in the merged list.
  const id =
    properties.osm_type && properties.osm_id
      ? `photon:${properties.osm_type}:${properties.osm_id}`
      : `photon:${position.lat},${position.lon}`;

  return { id, name, context, lat: position.lat, lon: position.lon, source: 'photon' };
}

async function photonFetch(query, { limit, timeoutMs, signal }) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const relay = () => controller.abort();
  signal?.addEventListener('abort', relay);
  try {
    const url = `${PHOTON_URL}?q=${encodeURIComponent(query)}&limit=${limit}`;
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    const data = await response.json();
    return Array.isArray(data?.features) ? data.features : [];
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', relay);
  }
}

// Best match for `name`, or null when nothing was found / the request
// failed. Never throws.
export async function geocodeName(name) {
  const query = (name || '').trim();
  if (!query) return null;

  const features = await photonFetch(query, { limit: 1, timeoutMs: TIMEOUT_MS });
  return coordinatesOf(features?.[0]);
}

// Candidate places for a partial name, in Photon's own order (it ranks by
// importance/population, the only useful signal this tier has -- callers must
// not re-sort). Never throws.
//
// [] means "the request succeeded and Photon knows nothing by that name";
// null means the request failed, timed out, or was aborted. The distinction
// is what lets the cache above decide what's worth remembering: a genuine
// empty answer is a fact, a failure is not.
export async function suggestGeocode(query, { limit = 5, signal } = {}) {
  const text = (query || '').trim();
  if (!text) return [];

  const features = await photonFetch(text, {
    limit,
    timeoutMs: SUGGEST_TIMEOUT_MS,
    signal,
  });
  if (!features) return null;
  return features.map(placeFromPhotonFeature).filter(Boolean);
}
